import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import { money, quantity, day } from "./format";

const C = {
  accent: "#345479", green: "#2d6a4f", red: "#9b2226", border: "#D9D3CC",
  cream: "#F5F2EF", beige: "#ECE7E2", charcoal: "#333332",
};

const s = {
  card: { background: "#fff", border: "1px solid #D9D3CC", borderRadius: 6, padding: 12, marginBottom: 8 },
  btn: { padding: "6px 14px", borderRadius: 4, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  input: { padding: "6px 8px", border: "1px solid #D9D3CC", borderRadius: 4, fontSize: 12, width: "100%" },
  sectionHdr: { fontSize: 13, fontWeight: 700, color: "#333332", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" },
};

const fmtMoney = (v) => v != null && v !== "" ? "$" + Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—";

const AMBER = "#b45309";
const today = () => new Date().toISOString().slice(0, 10);

// Per-currency totals as text. A krona note and a dollar share never add into
// one figure (CLAUDE.md §0.21).
const byCurrency = (totals) => {
  const parts = Object.entries(totals || {}).map(([ccy, v]) => money(v, { currency: ccy }));
  return parts.length ? parts.join(" + ") : money(0);
};
const sumByCurrency = (rows) => rows.reduce((acc, r) => {
  acc[r.currency] = (acc[r.currency] || 0) + Number(r.carrying_value || 0);
  return acc;
}, {});

const GROUPS = [
  ["holds", "Still held by this tranche"],
  ["converted", "What this tranche converted into — shares you can still sell; tick only if they are gone too"],
  ["other", "Other positions on this deal"],
];

/**
 * Writing off one tranche, not the whole deal.
 *
 * Brian, 3 November 2026: *"we need the ability to write off tranches and not
 * the whole investment"*. The first click asks the server which positions this
 * tranche still holds and prints them with their figures, ticked; what it
 * converted into and every other position on the deal are listed unticked.
 * Nothing is written until the second click, and only what is ticked.
 */
function TrancheWriteOff({ tranche, onClose, onDone }) {
  const [state, setState] = useState({ loading: true });
  const [chosen, setChosen] = useState([]);
  const [asOf, setAsOf] = useState(today());
  const [reason, setReason] = useState("");

  useEffect(() => {
    let live = true;
    api.post(`/securities/deal-tranches/${tranche.id}/write-off/`, {})
      .then((r) => {
        if (!live) return;
        setState({ preview: r.data });
        setChosen(r.data.proposed_lot_ids || []);
      })
      .catch((e) => live && setState({ error: e.response?.data?.error || e.message }));
    return () => { live = false; };
  }, [tranche.id]);

  const commit = async () => {
    setState((x) => ({ ...x, loading: true }));
    try {
      const r = await api.post(`/securities/deal-tranches/${tranche.id}/write-off/`, {
        confirm: true, lots: chosen, date: asOf, ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setState({ done: r.data });
      onDone();
    } catch (e) {
      setState({ error: e.response?.data?.error || e.message });
    }
  };

  const link = { border: 0, background: "none", textDecoration: "underline", cursor: "pointer", color: "inherit", font: "inherit", padding: 0 };
  const box = { marginTop: 10, padding: "10px 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.cream, fontSize: 12, color: C.charcoal };

  if (state.loading) return <div style={box}>Working…</div>;
  if (state.error) {
    return (
      <div style={{ ...box, color: C.red }}>
        {state.error} <button style={link} onClick={onClose}>Close</button>
      </div>
    );
  }
  if (state.done) {
    const d = state.done;
    return (
      <div style={box}>
        Written off <strong>{byCurrency(d.total_by_currency)}</strong> across {d.lot_count} position
        {d.lot_count === 1 ? "" : "s"} under this tranche, dated {day(d.as_of)}. {d.lot_count === 1 ? "It is" : "They are"} closed,
        off the dashboard and out of NAV. <button style={link} onClick={onClose}>Close</button>
      </div>
    );
  }

  const p = state.preview;
  if (!p.lots.length) {
    return <div style={box}>{p.note} <button style={link} onClick={onClose}>Close</button></div>;
  }
  const ticked = p.lots.filter((l) => chosen.includes(l.lot_id));
  const opens = [...new Set(ticked.flatMap((l) => l.opens_accounts || []))];
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  return (
    <div style={box}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Write off this tranche, not the whole deal</div>
      {p.note && <div style={{ marginBottom: 6, color: "#666" }}>{p.note}</div>}
      {GROUPS.map(([key, heading]) => {
        const rows = p.lots.filter((l) => l.group === key);
        if (!rows.length) return null;
        return (
          <div key={key} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>{heading}</div>
            {rows.map((l) => {
              const on = chosen.includes(l.lot_id);
              return (
                <label key={l.lot_id} style={{ display: "flex", gap: 7, alignItems: "flex-start", cursor: "pointer", marginBottom: 4 }}>
                  <input type="checkbox" checked={on} onChange={() => toggle(l.lot_id)} style={{ marginTop: 2 }} />
                  <span style={{ opacity: on ? 1 : 0.6 }}>
                    <strong>{l.security}</strong> — {quantity(l.quantity)} held, carrying {money(l.carrying_value, { currency: l.currency })}
                    {l.posts_journal ? ` → written off against ${l.asset_account}` : " → nothing to write off, closed only"}
                    <div style={{ color: "#888" }}>{l.why}</div>
                  </span>
                </label>
              );
            })}
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 8 }}>
        <div>
          <label style={{ fontSize: 11, color: "#888", display: "block" }}>Date of the write-off</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} style={{ ...s.input, width: 150 }} />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ fontSize: 11, color: "#888", display: "block" }}>Why (recorded on the journal and the position)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} style={s.input}
            placeholder={`Written off to zero — ${p.deal}, ${tranche.label || "this tranche"}`} />
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        Total written off: <strong>{byCurrency(sumByCurrency(ticked))}</strong>
        {ticked.some((l) => !l.posts_journal) && " · positions already at nil cost are closed with no journal"}
      </div>
      {opens.length > 0 && (
        <div style={{ marginBottom: 8, color: AMBER }}>Opens {opens.join(", ")} on the ledger for the first time.</div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={commit} disabled={!ticked.length || !asOf}
          style={{ ...s.btn, background: AMBER, color: "#fff", opacity: ticked.length && asOf ? 1 : 0.4 }}>
          Write off {ticked.length} of {p.lots.length}
        </button>
        <button onClick={onClose} style={{ ...s.btn, background: "transparent", border: `1px solid ${C.border}`, color: "#666" }}>Cancel</button>
      </div>
    </div>
  );
}

function emptyForm() {
  return { label: "", tranche_date: "", amount: "", face_value: "", oid_amount: "", notes: "" };
}

export default function DealTranchesPanel({ dealId }) {
  const [tranches, setTranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [writingOff, setWritingOff] = useState(null);

  // `quiet` reloads without the loading line, so a write-off's result stays on
  // screen while the tranche underneath picks up what was written off.
  const load = useCallback(async (quiet) => {
    if (!dealId) return;
    if (quiet !== true) setLoading(true);
    try {
      const r = await api.get("/securities/deal-tranches/", { params: { deal: dealId } });
      const list = r.data.results || r.data || [];
      list.sort((a, b) => (a.sort_order - b.sort_order) || ((a.tranche_date || "").localeCompare(b.tranche_date || "")));
      setTranches(list);
    } catch { setTranches([]); }
    setLoading(false);
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const totalAmount = tranches.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
  const totalFace = tranches.reduce((sum, t) => sum + (Number(t.face_value) || 0), 0);

  const startAdd = () => {
    const defaultLabel = tranches.length === 0 ? "Initial" : `Add-on #${tranches.length}`;
    setForm({ ...emptyForm(), label: defaultLabel });
    setEditingId(null);
    setShowForm(true);
    setError("");
  };

  const startEdit = (tr) => {
    setForm({
      label: tr.label || "",
      tranche_date: tr.tranche_date || "",
      amount: tr.amount || "",
      face_value: tr.face_value || "",
      oid_amount: tr.oid_amount || "",
      notes: tr.notes || "",
    });
    setEditingId(tr.id);
    setShowForm(true);
    setError("");
  };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        deal: dealId,
        label: form.label,
        tranche_date: form.tranche_date || null,
        amount: form.amount === "" ? null : parseFloat(form.amount),
        face_value: form.face_value === "" ? null : parseFloat(form.face_value),
        oid_amount: form.oid_amount === "" ? null : parseFloat(form.oid_amount),
        notes: form.notes,
        sort_order: editingId ? undefined : tranches.length,
      };
      if (editingId) {
        await api.put(`/securities/deal-tranches/${editingId}/`, payload);
      } else {
        await api.post("/securities/deal-tranches/", payload);
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm());
      await load();
    } catch (e) {
      setError(e.response?.data ? JSON.stringify(e.response.data) : "Save failed");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this tranche?")) return;
    try {
      await api.delete(`/securities/deal-tranches/${id}/`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!dealId) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={s.sectionHdr}>
        <span>Tranches ({tranches.length}){tranches.length > 0 && (
          <span style={{ marginLeft: 12, fontSize: 12, color: "#666", fontWeight: 500 }}>
            Total funded: <strong style={{ color: C.charcoal }}>{fmtMoney(totalAmount)}</strong>
            {totalFace > 0 && <> &nbsp;|&nbsp; Total face: <strong style={{ color: C.charcoal }}>{fmtMoney(totalFace)}</strong></>}
          </span>
        )}</span>
        <button style={{ ...s.btn, background: C.accent, color: "#fff" }} onClick={startAdd}>+ Add Tranche</button>
      </div>
      {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {loading ? (
        <div style={{ color: "#999", fontSize: 12 }}>Loading…</div>
      ) : tranches.length === 0 && !showForm ? (
        <div style={{ color: "#999", fontSize: 12, fontStyle: "italic", padding: "8px 0" }}>
          No tranches recorded. Add the initial funding event and any add-ons.
        </div>
      ) : tranches.map((tr, idx) => (
        <div key={tr.id} style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#888", minWidth: 18 }}>#{idx + 1}</span>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{tr.label || `Tranche ${idx + 1}`}</span>
              {tr.tranche_date && (
                <span style={{ fontSize: 12, color: "#666", fontFamily: "monospace", whiteSpace: "nowrap" }}>{tr.tranche_date}</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              <button style={{ ...s.btn, background: "transparent", border: `1px solid ${AMBER}`, color: AMBER, whiteSpace: "nowrap" }}
                onClick={() => setWritingOff(writingOff === tr.id ? null : tr.id)}>Write off…</button>
              <button style={{ ...s.btn, background: C.accent, color: "#fff" }} onClick={() => startEdit(tr)}>Edit</button>
              <button style={{ ...s.btn, background: C.red, color: "#fff" }} onClick={() => handleDelete(tr.id)}>{"×"}</button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, fontSize: 12, marginTop: 6, color: "#333" }}>
            <div><span style={{ color: "#888" }}>Amount:</span> <strong>{fmtMoney(tr.amount)}</strong></div>
            {tr.face_value && <div><span style={{ color: "#888" }}>Face:</span> <strong>{fmtMoney(tr.face_value)}</strong></div>}
            {tr.oid_amount && <div><span style={{ color: "#888" }}>OID:</span> <strong>{fmtMoney(tr.oid_amount)}</strong></div>}
          </div>
          {tr.written_off && (
            <div style={{ marginTop: 6, fontSize: 12, color: AMBER, fontWeight: 600 }}>
              Written off {day(tr.written_off.on)}: {byCurrency(tr.written_off.total_by_currency)} across{" "}
              {tr.written_off.lot_count} position{tr.written_off.lot_count === 1 ? "" : "s"}
              {" "}({tr.written_off.lots.map((l) => l.security).join(", ")})
            </div>
          )}
          {tr.notes && <div style={{ marginTop: 4, color: "#666", fontSize: 12, fontStyle: "italic" }}>{tr.notes}</div>}
          {writingOff === tr.id && (
            <TrancheWriteOff tranche={tr} onClose={() => setWritingOff(null)} onDone={() => load(true)} />
          )}
        </div>
      ))}

      {showForm && (
        <div style={{ ...s.card, background: C.beige }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            {editingId ? "Edit Tranche" : "Add Tranche"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: "#888" }}>Label</label>
              <input style={s.input} value={form.label} placeholder="Initial / Add-on #1"
                onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#888" }}>Date</label>
              <input style={s.input} type="date" value={form.tranche_date}
                onChange={(e) => setForm({ ...form, tranche_date: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#888" }}>Amount ($)</label>
              <input style={s.input} type="number" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#888" }}>Face Value ($)</label>
              <input style={s.input} type="number" step="0.01" value={form.face_value}
                onChange={(e) => setForm({ ...form, face_value: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "#888" }}>OID ($)</label>
              <input style={s.input} type="number" step="0.01" value={form.oid_amount}
                onChange={(e) => setForm({ ...form, oid_amount: e.target.value })} />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 11, color: "#888" }}>Notes</label>
            <input style={s.input} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button style={{ ...s.btn, background: C.green, color: "#fff" }}
              onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : (editingId ? "Update" : "Add Tranche")}
            </button>
            <button style={{ ...s.btn, background: "#999", color: "#fff" }}
              onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm()); setError(""); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
